import { LogoMark, MERCH_STORE_URL } from "@/assets";
import { SocialIcons } from "@/components/SocialIcons";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Phone, ShoppingBag, LifeBuoy } from "lucide-react";
import { useContact } from "@/components/useContent";

const EXPLORE = [
  ["The Label", "/about"],
  ["Leadership", "/team"],
  ["Services", "/services"],
  ["Beat Store", "/beats"],
  ["Merch", "/merch"],
  ["Book a Session", "/booking"],
  ["News & Press", "/news"],
  ["Medusa Room (VIP)", "/medusa"],
  ["Medusa Guide", "/medusa-guide"],
  ["Medusa Room Rules", "/medusa-terms"],
  ["Help Center", "/help"],
  ["FAQ", "/faq"],
  ["Request a Quote", "/quote"],
  ["Get Signed", "/talent"],
  ["Careers", "/careers"],
  ["The Roster", "/roster"],
  ["Terms & Policies", "/terms"],
];

export const Footer = () => {
  const navigate = useNavigate();
  const contact = useContact();
  return (
  <footer className="relative border-t border-white/8 bg-obsidian py-14" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
        <div>
          <div className="flex items-center gap-3">
            <LogoMark className="w-10 h-10" />
            <span className="font-display font-bold text-white text-sm">
              DGE: Cold-Blooded Records LLC™
            </span>
          </div>
          <p className="mt-4 text-sm text-ice/55 leading-relaxed max-w-xs">
            No Venom Needed ~ Just Pure Pressure. Independent record label &amp; production,
            led by Heather Da Boa and Sherrod &quot;HotRod&quot; Whiteside.
          </p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-venom">
            Official ASCAP® &amp; PRO Members
          </p>
          <SocialIcons className="mt-4 -ml-2" />
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ice/40 mb-4">Explore</p>
          <div className="flex flex-col gap-2.5">
            {EXPLORE.map(([label, to]) => (
              <button
                key={to}
                onClick={() => navigate(to)}
                className="text-left text-sm text-ice/65 hover:text-venom transition-colors duration-300"
                data-testid={`footer-link-${to.slice(1)}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ice/40 mb-4">Contact</p>
          <div className="flex flex-col gap-2.5">
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-2 text-sm text-ice/65 hover:text-venom transition-colors duration-300 break-all"
              data-testid="footer-contact-email"
            >
              <Mail size={14} className="shrink-0 text-crystal" /> {contact.email}
            </a>
            <a
              href={`mailto:${contact.support}?subject=Support%20Request`}
              className="flex items-center gap-2 text-sm text-ice/65 hover:text-venom transition-colors duration-300 break-all"
              data-testid="footer-contact-support"
            >
              <LifeBuoy size={14} className="shrink-0 text-crystal" /> {contact.support}
            </a>
            <a
              href={`mailto:${contact.press}`}
              className="flex items-center gap-2 text-sm text-ice/65 hover:text-venom transition-colors duration-300 break-all"
              data-testid="footer-contact-press"
            >
              <Mail size={14} className="shrink-0 text-crystal" /> {contact.press}
            </a>
            {contact.phones.map((p) => (
              <a
                key={p}
                href={`tel:${p.replace(/[^0-9]/g, "")}`}
                className="flex items-center gap-2 text-sm text-ice/65 hover:text-venom transition-colors duration-300"
                data-testid={`footer-contact-phone-${p.replace(/[^0-9]/g, "")}`}
              >
                <Phone size={14} className="shrink-0 text-crystal" /> {p}
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ice/40 mb-4">Label</p>
          <div className="flex flex-col gap-2.5">
            <span className="text-sm text-ice/65">Est. 2026</span>
            <a
              href={MERCH_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-ice/65 hover:text-venom transition-colors duration-300"
              data-testid="footer-merch-link"
            >
              <ShoppingBag size={14} className="text-crystal" /> the33rd.store
            </a>
            <Link to="/admin" className="text-sm text-ice/65 hover:text-crystal transition-colors duration-300" data-testid="footer-admin-link">
              Label Login
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-7 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-ice/45 text-center sm:text-left" data-testid="footer-copyright">
          Copyright © 2026 DGE: Cold-Blooded Records LLC™. All Rights Reserved.
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ice/30">
          ASCAP® is a registered trademark of ASCAP
        </p>
      </div>
    </div>
  </footer>
  );
};
